import exportApi from "@services/exports";
import commonApi from "@services/common.js";
import mediaAssetsApi from "@services/mediaAssets.js";
import eventLogger from "@services/ApiLogger/EventLogger.js";
import { generateNewNameMultiLanguage as generateNewName } from "@util/generateNewName";
import { Guid } from "@/util/guid";
import erpApi from "@services/erp.js";
import catalogApi from "@services/catalogs.js";
import i18n from "@/i18n";
import { ExportType, LayoutFileType } from "@medapro-types/Exports/export-enum";
import { enumToArray } from "@util/enums/enum-util";

const { t } = i18n.global;

const loadingTasks = {
  exportLoader: null,
  mappingsLoader: null,
  ruleProfilesLoader: null,
  regionsLoader: null,
  additionalFiltersLoader: null
};

const defaultState = () => ({
  loading: false,
  showManageExport: false,
  isSaving: false,
  updateSeen: true,
  lastRunSuccess: true,
  name: "",
  catalogs: [],
  mappings: [],
  ruleProfiles: [],
  languages: [],
  regions: [],
  additionalFilters: [],
  macroTemplates: [],
  encodings: [],
  fastPageFormats: [
    {
      id: "83ee4af7-8f75-4ee2-abd7-f23ca81ef949",
      name: "docx"
    },
    {
      id: "7e94c8ba-2f16-401c-ad92-cfe2fb5ec412",
      name: "xlsx"
    },
    {
      id: "ce6a98ad-08c8-4baa-8833-b454edfb192c",
      name: "pptx"
    },
    {
      id: "aee5ec6f-23ad-4366-a30b-2be586a467ab",
      name: "pdf"
    },
    {
      id: "d5467d59-ada7-4ced-a479-ac76e33ae353",
      name: "png"
    },
    {
      id: "cd747823-38d9-4236-aecd-f0042026f584",
      name: "jpeg"
    },
    {
      id: "1cc4370e-a624-4aff-9615-ebccdc5024a3",
      name: "xml"
    },
    {
      id: "e3cbea2d-36c3-427f-bd9f-ae2a79649569",
      name: "xps"
    },
    {
      id: "cfc67b2a-6d7e-4729-8283-3e48ca3d4f6b",
      name: "rtf"
    },
    {
      id: "7c01185e-d86f-48d7-b8fa-86e93aaef251",
      name: "html"
    }
  ],
  fastPageOutputOptions: [
    {
      id: "931c9703-9640-4116-9618-69ae58e05f85",
      name: "singleFile"
    },
    {
      id: "38362e22-d525-415d-9930-3a7430e0ed9b",
      name: "singleFilePerLayout"
    },
    {
      id: "52672e6a-219f-463b-89a3-17afb43457b0",
      name: "singleVariationGroupPerFile"
    },
    {
      id: "b3d8a264-41f3-41bf-8e35-d69761cb1442",
      name: "singleArticlePerFile"
    }
  ],
  fieldSeparators: [],
  fieldLimiters: [],
  erpProfiles: [],
  selectedProgressInfo: {},
  territories: [],
  currencies: [],
  priceFlags: [],
  catalogVersion: "",
  exportProjects: [],
  bmeCatSupplierIdTypes: [],
  bmeCatBuyerIdTypes: [],
  enableUpload: false,
  mappingTargets: [],
  layoutTypes: [],
  isModified: false,
  hasNameValidationError: false,
  tradeProChannels: new Map(),
  isTradeProChannelEnabled: false,
  articleMediaAssets: []
});

export default {
  namespaced: true,
  state: { ...defaultState() },
  getters: {
    hasUnsavedData(state) {
      return state.isModified;
    }
  },
  mutations: {
    "reset-state"(state) {
      Object.assign(state, defaultState());
    },
    "set-store-loading": function (state, args) {
      state.loading = args;
    },
    "set-show-manage-export": function (state, args) {
      state.showManageExport = args;
    },
    "set-update-seen": function (state, args) {
      state.updateSeen = args;
    },
    "set-last-run-success": function (state, args) {
      state.lastRunSuccess = args;
    },
    "set-is-saving": function (state, args) {
      state.isSaving = args;
    },
    "set-export-project-name": function (state, args) {
      state.name = args;
    },
    "set-progress-info": function (state, args) {
      state.selectedProgressInfo = args;
    },
    "set-enable-upload": function (state, args) {
      state.enableUpload = args;
    },
    "update-catalogs": function (state, args) {
      state.catalogs = args;
    },
    "update-mappings": function (state, args) {
      state.mappings = args;
    },
    "update-rule-profiles": function (state, args) {
      state.ruleProfiles = args;
    },
    "update-regions": function (state, args) {
      state.regions = args;
    },
    "update-additional-filters": function (state, args) {
      state.additionalFilters = args;
    },
    "update-macro-templates": function (state, args) {
      state.macroTemplates = args;
    },
    "update-erp-profiles": function (state, args) {
      state.erpProfiles = args;
    },
    "update-encodings": function (state, args) {
      state.encodings = args;
    },
    "update-field-separators": function (state, args) {
      state.fieldSeparators = args;
    },
    "update-field-limiters": function (state, args) {
      state.fieldLimiters = args;
    },
    "update-territories": function (state, args) {
      state.territories = args;
    },
    "update-currencies": function (state, args) {
      state.currencies = args;
    },
    "update-priceFlags": function (state, args) {
      state.priceFlags = args;
    },
    "update-catalogVersion": function (state, args) {
      state.catalogVersion = args;
    },
    "update-export-projects": function (state, args) {
      state.exportProjects = args;
    },
    "update-bme-cat-supplier-id-types": function (state, args) {
      state.bmeCatSupplierIdTypes = args;
    },
    "update-bme-cat-buyer-id-types": function (state, args) {
      state.bmeCatBuyerIdTypes = args;
    },
    "update-mapping-targets": function (state, args) {
      state.mappingTargets = args;
    },
    "delete-export": function (state, args) {
      state.exportProjects = state.exportProjects.filter((x) => !args.includes(x.exportId));
    },
    "set-has-name-validation-error": function (state, args) {
      state.hasNameValidationError = args;
    },
    "add-or-update-export-project": function (state, args) {
      const index = state.exportProjects.findIndex((item) => item.exportId === args.exportId);

      if (index !== -1) {
        // Update existing export
        state.exportProjects.splice(index, 1, args);
      } else {
        // Add new export
        state.exportProjects.push(args);
      }
    },
    "update-fastpage-layout-types": function (state, args) {
      state.layoutTypes = args;
    },
    "set-modified-status"(state, args) {
      state.isModified = args;
    },
    "set-tradepro-channels"(state, args) {
      state.tradeProChannels = new Map(args.map((item) => [item.code, item]));
    },
    "set-default-tradepro-channels"(state) {
      const tradeProChannelsMap = new Map();
      tradeProChannelsMap.set("TRADEPRO_WEB", { code: "TRADEPRO_WEB", name: "TRADEPRO_WEB", enabled: true });
      state.tradeProChannels = tradeProChannelsMap;
    },
    "set-is-tradepro-channel-enabled"(state, args) {
      state.isTradeProChannelEnabled = args;
    },
    "set-media-assets"(state, args) {
      state.articleMediaAssets = args;
    }
  },
  actions: {
    saveExport: async function (context, args) {
      const logContext = eventLogger.beginContext("saveExport");
      context.dispatch("setIsSaving", true);
      context.dispatch("setNotificationSeen", false);
      let exportId = args.dataForExport.exportId ?? Guid.empty;
      try {
        if (exportId === Guid.empty) {
          exportId = await exportApi.createExport(args.dataForExport);
          context.dispatch("setNotificationSeen", false);
          context.dispatch("setLastRunSuccess", true);
        } else {
          await exportApi.updateExport(args.dataForExport);
        }

        args.dataForExport.exportId = exportId;
        await context.dispatch("addOrUpdateExportProject", args.dataForExport);
        context.dispatch("setLastRunSuccess", true);
        context.dispatch("setIsSaving", false);
      } catch (error) {
        context.dispatch("setIsSaving", false);
        context.dispatch("setLastRunSuccess", false);

        if (error.isAxiosError && error.response && error.response.data?.error == "NameExist") {
          context.commit("set-has-name-validation-error", true);
        } else {
          logContext.error("Export save completed with errors", { ...args, exception: error }).alsoToConsole();
        }
        throw error;
      } finally {
        logContext.complete();
      }

      return exportId;
    },
    getAllExports: async function ({ commit, state, rootState }, args) {
      if (!args?.force && state.exportProjects.length) {
        return;
      }
      if (loadingTasks.exportLoader) {
        return await loadingTasks.exportLoader;
      }

      const primaryLanguage = rootState.metadata.contentLanguages.enabled[0].languageCode;
      loadingTasks.exportLoader = exportApi.getExports(args.type, primaryLanguage);
      try {
        const exports = await loadingTasks.exportLoader;
        commit("update-export-projects", exports);
      } finally {
        loadingTasks.exportLoader = null;
      }
    },
    addOrUpdateExportProject: async function ({ commit }, args) {
      commit("add-or-update-export-project", args);
    },

    setProgressInfo: function ({ commit }, args) {
      commit("set-progress-info", args);
    },
    setStoreLoading: function ({ commit }, args) {
      commit("set-store-loading", args);
    },
    setShowManageExport: function ({ commit }, args) {
      commit("set-show-manage-export", args);
    },
    setNotificationSeen: function ({ commit }, args) {
      commit("set-update-seen", args);
    },
    setIsSaving: function ({ commit }, args) {
      commit("set-is-saving", args);
    },
    setLastRunSuccess: function ({ commit }, args) {
      commit("set-last-run-success", args);
    },
    setEnableUpload: function ({ commit }, args) {
      commit("set-enable-upload", args);
    },
    setModifiedStatus: function ({ commit }, args) {
      commit("set-modified-status", args);
    },
    generateExportNameByType: async function ({ commit, state, dispatch }, args) {
      await dispatch("getAllExports", args.exportType);
      const newName = generateNewName(
        args.name != null ? args.name : "New export project",
        state.exportProjects.map((x) => {
          return { de: x };
        }),
        "de"
      );
      commit("set-export-project-name", newName);
    },
    loadCatalogs: async function ({ commit }, args) {
      const catalogs = await exportApi.getCatalogs(args.language, args.exportType);
      commit("update-catalogs", catalogs);
    },
    loadMappings: async function ({ commit }, args) {
      if (loadingTasks.mappingsLoader) {
        return loadingTasks.mappingsLoader;
      }
      loadingTasks.mappingsLoader = exportApi.getCatalogMappingProjects(args.catalogId, args.exportType);

      try {
        let mappings = await loadingTasks.mappingsLoader;
        if (mappings.length == 0) {
          mappings = [{ name: `<${t("none")}>`, catalogMappingProjectId: null }, ...mappings];
        }
        const modifiedMappings = mappings.map((mapping) => ({
          ...mapping,
          mappingNameWithPrefix:
            mapping.autoMappingProfileId && !Guid.isEmpty(mapping.autoMappingProfileId)
              ? `${t("exports.automapping")} ${mapping.autoMappingProfileName} ${!Guid.isEmpty(mapping.catalogMappingProjectId) ? `(${t("exports.mappingProject")} ${mapping.name} - ${mapping.targetSystemName})` : `(${mapping.targetSystemName})`}`
              : `${t("exports.mappingProject")} ${mapping.name} (${mapping.targetSystemName})`
        }));

        commit("update-mappings", modifiedMappings);
      } finally {
        loadingTasks.mappingsLoader = null;
      }
    },
    loadRuleProfiles: async function ({ commit }, args) {
      if (loadingTasks.ruleProfilesLoader) {
        return loadingTasks.ruleProfilesLoader;
      }
      loadingTasks.ruleProfilesLoader = args.catalogMappingTargetId ? exportApi.getRuleProfiles(args.catalogMappingTargetId) : Promise.resolve([]);

      try {
        const ruleProfiles = [{ name: `<${t("none")}>`, ruleProfileId: null }, ...(await loadingTasks.ruleProfilesLoader)];
        commit("update-rule-profiles", ruleProfiles);
      } finally {
        loadingTasks.ruleProfilesLoader = null;
      }
    },
    loadRegions: async function ({ commit }, args) {
      if (loadingTasks.regionsLoader) {
        return loadingTasks.regionsLoader;
      }
      try {
        loadingTasks.regionsLoader = commonApi.getLanguageRegions(args.language, args.currentLocale);
        const regions = await loadingTasks.regionsLoader;
        let mappedRegions = Object.entries(regions).map((x) => ({
          text: x[0],
          value: x[1]
        }));
        mappedRegions = [{ text: null, value: `<${t("none")}>` }, ...mappedRegions];
        commit("update-regions", mappedRegions);
      } finally {
        loadingTasks.regionsLoader = null;
      }
    },
    loadAdditionalFilters: async function ({ commit }) {
      let filters = await exportApi.getArticleFilters();
      filters = [{ name: `<${t("none")}>`, catalogFilterId: null, isEverywhereAdvancedSearch: true }, ...filters];
      commit("update-additional-filters", filters);
    },
    loadMacroTemplates: async function ({ commit }) {
      let macroTemplates = await exportApi.getExcelMacros();
      macroTemplates = [null, ...macroTemplates];
      commit("update-macro-templates", macroTemplates);
    },
    loadErpProfiles: async function ({ commit }) {
      const profiles = await erpApi.getErpProfiles();
      commit("update-erp-profiles", profiles);
    },
    loadEncodings: async function ({ commit }) {
      const useBOMSpecificEncodings = true;
      const encodings = await commonApi.getEncodings(useBOMSpecificEncodings);
      commit("update-encodings", encodings);
    },
    loadFieldSeparators: async function ({ commit }) {
      const separators = [
        {
          id: ";",
          name: "semicolon"
        },
        {
          id: ",",
          name: "comma"
        },
        {
          id: "|",
          name: "pipe"
        },
        {
          id: "\t",
          name: "tab"
        }
      ];
      commit("update-field-separators", separators);
    },
    loadFieldLimiters: async function ({ commit }) {
      const limiters = [
        {
          id: "\0",
          name: "none"
        },
        {
          id: '"',
          name: "quote"
        },
        {
          id: "'",
          name: "apostrophe"
        }
      ];
      commit("update-field-limiters", limiters);
    },
    loadTerritories: async function ({ commit }) {
      const territories = require("./territories.json");
      commit("update-territories", territories);
    },
    loadCurrencies: async function ({ commit }) {
      const currencies = [
        {
          id: "67E95655-B4A6-4BF3-9B77-13656671C212",
          name: null
        },
        {
          id: "9AAF1DC3-07B8-4FBF-AE21-79547BED7CE6",
          name: "EUR"
        },
        {
          id: "16A543CB-DB50-42C1-BB26-FF8376945F78",
          name: "USD"
        },
        {
          id: "D3D69F3D-B6B0-43DB-A1AC-8EE355A05053",
          name: "GBP"
        }
      ];
      commit("update-currencies", currencies);
    },
    loadPriceFlags: async function ({ commit }) {
      const priceFlags = [
        {
          id: "9AAF1DC3-07B8-4FBF-AE21-79547BED7CE6",
          isChecked: false,
          name: "Incl.freight charges"
        },
        {
          id: "16A543CB-DB50-42C1-BB26-FF8376945F78",
          isChecked: false,
          name: "Incl.Packing costs"
        },
        {
          id: "D3D69F3D-B6B0-43DB-A1AC-8EE355A05053",
          isChecked: false,
          name: "Incl.insurence costs"
        },
        {
          id: "372331E5-E5EC-40AB-BE72-82478EB84A79",
          isChecked: false,
          name: "Incl.payment of duty"
        }
      ];

      commit("update-priceFlags", priceFlags);
    },
    loadCatalogVersion: async function ({ commit }) {
      commit("update-catalogVersion", "1.0");
    },
    loadBmeCatSupplierData: async function ({ commit }) {
      const bmeCatSupplierIdTypes = [
        {
          id: "F2FF4B42-20D8-44CD-9BCA-3BAB8371A246",
          name: "buyer_specific"
        },
        {
          id: "FFE2ACE9-C65F-464C-A4A2-A19325C7C4BC",
          name: "customer_specific"
        },
        {
          id: "64DD1BFA-302B-4379-8725-E513BF66F39D",
          name: "duns"
        },
        {
          id: "6716E6FC-D172-47E1-9E7D-62F2EF483B95",
          name: "iln"
        },
        {
          id: "BB585783-819B-427F-A0F6-327C02F8BB53",
          name: "gln"
        },
        {
          id: "BB585783-869B-427F-A0F6-327C02F8BB53",
          name: "party_specific"
        },
        {
          id: "BB585783-869B-427F-A0F6-327C02F83B53",
          name: "supplier_specific"
        }
      ]; //api call to get supplier data

      //api Simulieren
      return await new Promise((res) => {
        setTimeout(
          () => {
            res(commit("update-bme-cat-supplier-id-types", bmeCatSupplierIdTypes));
          },
          randomIntFromInterval(500, 1500)
        );
      });
    },
    loadBmeCatBuyerData: async function ({ commit }) {
      const bmeCatBuyerIdTypes = [
        {
          id: "F2FF4B42-20D8-44CD-9BCA-3BAB8371A246",
          name: "buyer_specific"
        },
        {
          id: "64DD1BFA-302B-4379-8725-E513BF66F39D",
          name: "duns"
        },
        {
          id: "6716E6FC-D172-47E1-9E7D-62F2EF483B95",
          name: "iln"
        },
        {
          id: "BB585783-869B-427F-A0F6-327C02F83B53",
          name: "supplier_specific"
        }
      ]; //api call to get supplier data

      //api Simulieren
      return await new Promise((res) => {
        setTimeout(
          () => {
            res(commit("update-bme-cat-buyer-id-types", bmeCatBuyerIdTypes));
          },
          randomIntFromInterval(500, 1500)
        );
      });
    },
    loadMappingTargets: async function ({ commit }) {
      const mappingTargets = await catalogApi.getMappingTargets({
        catalogType: 1
      });
      commit("update-mapping-targets", mappingTargets);
    },
    //use during mount and extend if needed
    loadDataForCardIfNeeded: async function ({ state, dispatch }, args) {
      let ownLoading = false;
      if (!state.loading) {
        ownLoading = true;
        dispatch("setStoreLoading", true);
      }
      switch (args.identifier) {
        case "dataSelection":
          await dispatch("loadDataForDataSelection", args);
          break;
        case "catalogue":
          await dispatch("loadDataForCatalogue");
          break;
        case "supplier":
          await dispatch("loadDataForSupplier");
          break;
        case "buyer":
          await dispatch("loadDataForBuyer");
          break;
        case "layouts":
          await dispatch("loadDataForLayouts");
          break;
        default:
          break;
      }
      if (ownLoading) {
        dispatch("setStoreLoading", false);
      }
    },
    loadDataForDataSelection: async function ({ state, dispatch }, args) {
      await dispatch("loadCatalogs", args);
      if (!state.languages || !state.languages.length) await dispatch("loadLanguages", args);
      if (!state.additionalFilters || !state.additionalFilters.length) await dispatch("loadAdditionalFilters");
      if (!state.macroTemplates || !state.macroTemplates.length) await dispatch("loadMacroTemplates");
      if (!state.encodings || !state.encodings.length || args.exportType == ExportType.Text || args.exportType == ExportType.BMEcat)
        await dispatch("loadEncodings", args);
      if (!state.fieldSeparators || !state.fieldSeparators.length) await dispatch("loadFieldSeparators");
      if (!state.fieldLimiters || !state.fieldLimiters.length) await dispatch("loadFieldLimiters");
      if (!state.erpProfiles || !state.erpProfiles.length) await dispatch("loadErpProfiles", args);
      if (!state.mappingTargets || !state.mappingTargets.length) await dispatch("loadMappingTargets", args);
    },
    loadDataForSupplier: async function ({ state, dispatch }) {
      const promises = [];
      if (!state.bmeCatSupplierIdTypes || !state.bmeCatSupplierIdTypes.length) promises.push(dispatch("loadBmeCatSupplierData"));

      return promises;
    },
    loadDataForBuyer: async function ({ state, dispatch }) {
      const promises = [];
      if (!state.bmeCatBuyerIdType || !state.bmeCatBuyerIdType.length) promises.push(dispatch("loadBmeCatBuyerData"));

      return promises;
    },
    loadDataForCatalogue: async function ({ state, dispatch }) {
      if (!state.territories || !state.territories.length) await dispatch("loadTerritories");
      if (!state.currencies || !state.currencies.length) await dispatch("loadCurrencies");
      if (!state.priceFlags || !state.priceFlags.length) await dispatch("loadPriceFlags");
      if (!state.catalogVersion) await dispatch("loadCatalogVersion");
    },

    removeExport: async function ({ commit }, exportId) {
      const formatedExportId = Array.isArray(exportId) ? exportId : [exportId];
      try {
        await exportApi.delete(formatedExportId);
        commit("delete-export", exportId);
      } catch (error) {
        console.error("failed to delete export", error);
      }
    },
    loadDataForLayouts: async function ({ state, dispatch }) {
      const promises = [];
      if (!state.layoutTypes || !state.layoutTypes.length) promises.push(dispatch("loadLayoutTypes"));
      return promises;
    },
    loadLayoutTypes: async function ({ commit }) {
      const layoutTypes = enumToArray(LayoutFileType);

      commit("update-fastpage-layout-types", layoutTypes);
    },
    loadMediaAssets: async function ({ rootState, commit }) {
      const enabledLanguages = rootState.metadata.contentLanguages.enabled;
      let languages = enabledLanguages
        .map((x) => [x.languageCode, x.fallbackLanguageCode])
        .flat()
        .filter((x) => !!x);
      languages = Array.from(new Set(languages));
      const targetType = "Article";
      const mediaAssets = await mediaAssetsApi.get(languages, targetType);
      const filteredMediaAssets = mediaAssets.reduce(
        (acc, item) =>
          item[languages[0]].convertedFromId === null
            ? acc.concat(
                Object.fromEntries(
                  Object.entries(item).map(([lang, m]) => [
                    lang,
                    {
                      mediaAssetName: m.mediaAssetName,
                      mediaAssetId: m.mediaAssetId,
                      convertedFromId: m.convertedFromId
                    }
                  ])
                )
              )
            : acc,
        []
      );

      commit("set-media-assets", filteredMediaAssets);
    }
  }
};

//#region Utility
function randomIntFromInterval(min, max) {
  // min and max included
  return Math.floor(Math.random() * (max - min + 1) + min);
}
//#endregion
